import { DestroyRef, inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { I18nService } from '@core/i18n-legacy';
import {
  flexiblePositionStrategyFactory,
  Step,
  WalkthroughStepsService,
  WalkthroughTooltipComponent,
} from '@core/walkthrough-legacy';

@Injectable({ providedIn: 'root' })
export class AaImprovementsWalkthroughService {
  private readonly walkthroughStepsService = inject(WalkthroughStepsService);
  private readonly i18n = inject(I18nService);
  private readonly destroyRef = inject(DestroyRef);

  private steps = [
    {
      id: 'walkthrough-step-aa-improvements-1',
      position: 1,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['top', 'left'],
          heading: this.i18n.translate(
            'app.offer_form.walkthrough_tooltip.aa_improvements.other_actions.heading',
          ),
          description: this.i18n.translate(
            'app.offer_form.walkthrough_tooltip.aa_improvements.other_actions.description',
          ),
          ctaText: this.i18n.translate('app.common.got_it'),
          showStepCounter: false,
          hasNewBadge: true,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'top' }],
        offset: { offsetX: 24, offsetY: -40 },
      },
    },
    {
      id: 'walkthrough-step-aa-improvements-2',
      position: 2,
      component: {
        definition: WalkthroughTooltipComponent,
        inputs: {
          arrowConfig: ['left', 'bottom'],
          heading: this.i18n.translate(
            'app.offer_form.walkthrough_tooltip.aa_improvements.send_emails.heading',
          ),
          description: this.i18n.translate(
            'app.offer_form.walkthrough_tooltip.aa_improvements.send_emails.description',
          ),
          ctaText: this.i18n.translate('app.common.got_it'),
          showStepCounter: false,
          hasNewBadge: true,
        },
      },
      renderOptions: {
        strategy: flexiblePositionStrategyFactory,
        useWalkthroughBackdropClass: true,
        positions: [{ originX: 'center', originY: 'top', overlayX: 'start', overlayY: 'bottom' }],
        offset: { offsetX: -36, offsetY: -16 },
      },
    },
  ];

  public start(hasFirstStep: boolean, hasSecondStep: boolean): void {
    this.walkthroughStepsService
      .start(this.getFilteredSteps(hasFirstStep, hasSecondStep))
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe();
  }

  private getFilteredSteps(hasFirstStep: boolean, hasSecondStep: boolean): Step[] {
    return [
      ...(hasFirstStep
        ? [
            <Step>{
              ...this.steps[0],
              component: {
                ...this.steps[0].component,
                inputs: {
                  ...this.steps[0].component.inputs,
                  showStepCounter: hasSecondStep,
                  ctaText: hasSecondStep
                    ? this.i18n.translate('app.onboarding.walkthrough_tooltip.next_step')
                    : this.i18n.translate('app.common.got_it'),
                },
              },
            },
          ]
        : []),
      ...(hasSecondStep
        ? [
            <Step>{
              ...this.steps[1],
              component: {
                ...this.steps[1].component,
                inputs: {
                  ...this.steps[1].component.inputs,
                  showStepCounter: hasFirstStep,
                },
              },
            },
          ]
        : []),
    ];
  }
}
